import { PDP_VARIANT_DISPLAY_ORDER, type VariantKey } from '@libs/common-types';

import { ALGOLIA_COLOR_MAP } from '../constants';
import type { MappedProductVariant, ProductHit, VariantValue } from '../types';

type GetVariant = (product: MappedProductVariant) => VariantValue | null;

export const getHexColor = (color?: string) =>
    ALGOLIA_COLOR_MAP[color as keyof typeof ALGOLIA_COLOR_MAP] ?? ALGOLIA_COLOR_MAP.White;

const getMultiColorValue = (value: string) => {
    const [firstValue, secondValue] = value.split(' & ');

    return secondValue ? `${getHexColor(firstValue)},${getHexColor(secondValue)}` : getHexColor(firstValue);
};

export const getColorName = (colors?: string[], hexColor?: string) =>
    (colors ?? []).find(color => ALGOLIA_COLOR_MAP[color as keyof typeof ALGOLIA_COLOR_MAP] === hexColor);

const getSizeVariant: GetVariant = product => {
    if (!product.size || Object.values(product.size ?? {}).length === 0) {
        return null;
    }

    const [key, value] = Object.entries(product.size)[0];

    return {
        key: `${key}-${value}`,
        value,
        display: value,
    };
};

const getFlavorVariant: GetVariant = product => {
    if (!product.flavor || Object.values(product.flavor ?? {}).length === 0) {
        return null;
    }

    return {
        key: product.flavor.value,
        value: product.flavor.value,
        display: product.flavor?.displays,
    };
};

const getColorVariant: GetVariant = product => {
    if ((!product.color?.swatchDisplay && !product.color?.value) || Object.values(product.color ?? {}).length === 0) {
        return null;
    }

    // Uses 'color.value' as it is the primary color identifier to prevent hex collisions, with 'color.swatchDisplay' as a fallback (not all color.value have a mapping)
    const value =
        getMultiColorValue(product.color.value ?? '') === ALGOLIA_COLOR_MAP.White
            ? getMultiColorValue(product.color.swatchDisplay ?? '')
            : getMultiColorValue(product.color.value ?? '');

    return {
        key: value,
        value,
        display: product.color.value,
    };
};

const getWattageVariant: GetVariant = product => {
    if (!product.wattage) {
        return null;
    }

    return {
        key: product.wattage,
        value: product.wattage,
        display: product.wattage,
    };
};

const getQuantityVariant: GetVariant = product => {
    if (!product.quantity) {
        return null;
    }

    return {
        key: product.quantity,
        value: product.quantity,
        display: product.quantity,
    };
};

// TODO: extend the mapping with other types once we have a more stable data sample.
const variantMappers: Record<VariantKey, GetVariant> = {
    flavor: getFlavorVariant,
    size: getSizeVariant,
    color: getColorVariant,
    quantity: getQuantityVariant,
    wattage: getWattageVariant,
};

export const mapProductVariant = (product: ProductHit): MappedProductVariant => {
    const variants = {} as Record<string, VariantValue>;

    PDP_VARIANT_DISPLAY_ORDER.forEach(key => {
        if (product[key as keyof typeof product]) {
            const result = variantMappers[key](product);

            if (result) {
                variants[key] = result;
            }
        }
    });

    // Overwrite the color value with the mapped hex value
    const color = getMappedColorProperty(product);

    return { ...product, color, variants };
};

export function getMappedColorProperty(product: ProductHit) {
    if (!(product.color?.swatchDisplay || product.color?.value) || Object.values(product.color ?? {}).length === 0) {
        return undefined;
    }

    const swatchDisplay = product.color.value;
    // Uses 'color.value' as it is the primary color identifier to prevent hex collisions, with 'color.swatchDisplay' as a fallback (not all color.value have a mapping)
    const value =
        getMultiColorValue(product.color.value ?? '') === ALGOLIA_COLOR_MAP.White
            ? getMultiColorValue(product.color.swatchDisplay ?? '')
            : getMultiColorValue(product.color.value ?? '');

    return {
        ...product.color,
        swatchDisplay,
        value,
    };
}
